import React, { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import { apiFetch } from "../../../apiFetch";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./PlayerVideoProfile.css";

const PlayerVideoProfile = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const profileSlug =
    location.state?.playerProfileSlug ||
    params.profileSlug ||
    params.slug ||
    params.playerSlug ||
    params.playerId ||
    "";


  const storedRole =
    (localStorage.getItem("userRole") || localStorage.getItem("role") || "").toLowerCase();

  const viewerRole = (location.state?.viewerRole || storedRole || "player").toLowerCase();
  const isRoleView = ["agent", "scout", "club"].includes(viewerRole);

  const viewerId =
    location.state?.viewerId ||
    (viewerRole === "player"
      ? localStorage.getItem("playerId")
      : viewerRole === "agent"
        ? localStorage.getItem("agentId")
        : viewerRole === "scout"
          ? localStorage.getItem("scoutId")
          : viewerRole === "club"
            ? localStorage.getItem("clubId")
            : null);

  const [resolvedPlayerId, setResolvedPlayerId] = useState(null);

  // 🔁 Choose from props, URL param, or navigation state
  const playerId =
    props.playerId ||
    location.state?.playerId ||
    params.playerId ||
    resolvedPlayerId ||
    null;

  const isOwner =
    viewerRole === "player" &&
    viewerId &&
    playerId &&
    String(viewerId) === String(playerId);

  const [videos, setVideos] = useState([]);
  const [channelUrl, setChannelUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChannelId, setHasChannelId] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [noVideosFound, setNoVideosFound] = useState(false);

  // ⭐ NEW: per-viewer ratings
  const [userRatings, setUserRatings] = useState({}); // youtube_video_id -> rating

  // 📊 NEW: per-video progress (% watched)
  const [progressByVideo, setProgressByVideo] = useState({});

  // 🚨 report feedback
  const [reportedVideos, setReportedVideos] = useState({});

  const [reportFeedback, setReportFeedback] = useState({
    videoId: null,
    text: "",
  }); // youtube_video_id -> number (0..100)

  // 🎬 NEW: open/close video state (videos do NOT disappear on this page)
  const [openVideoId, setOpenVideoId] = useState(null);
  const openVideoIdRef = useRef(null);
  useEffect(() => {
    openVideoIdRef.current = openVideoId;
  }, [openVideoId]);

  // ✅ YouTube player refs for accurate tracking (same as Feed.jsx)
  const ytPlayersRef = useRef({}); // videoId -> player instance
  const watchIntervalRef = useRef(null);
  const lastPlaybackTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const activeVideoIdRef = useRef(null);
  const clientExpectedRef = useRef(0);

  const activeSessionIdRef = useRef(null);
  const eventCounterRef = useRef(0);

  const makeClientId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const beginWatchSession = (videoId) => {
    activeVideoIdRef.current = String(videoId);
    activeSessionIdRef.current = makeClientId();
    eventCounterRef.current = 0;
  };

  const nextEventId = () => {
    eventCounterRef.current += 1;
    const sessionId = activeSessionIdRef.current || "no-session";
    return `${sessionId}-${eventCounterRef.current}`;
  };


  // ✅ Posted-time helpers (ONLY shown to scout/agent/club)
  const toDate = (v) => {
    const raw =
      v?.published_at || v?.publishedAt || v?.publishedAtMs || v?.createdAt || null;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  const timeAgo = (d) => {
    if (!d) return "";
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / (1000 * 60));
    const hrs = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  


  const stopWatchTracking = ({ resetSession = true } = {}) => {
    if (watchIntervalRef.current) {
      clearInterval(watchIntervalRef.current);
      watchIntervalRef.current = null;
    }

    clientExpectedRef.current = 0;
    lastPlaybackTimeRef.current = 0;
    isPlayingRef.current = false;
    activeVideoIdRef.current = null;

    if (resetSession) {
      activeSessionIdRef.current = null;
      eventCounterRef.current = 0;
    }

    // ✅ Do NOT delete player refs to avoid close→reopen race
  };




  const flushActiveWatchProgress = async () => {
    try {
      if (!viewerId || !viewerRole) return;
      if (!isPlayingRef.current) return;

      const videoId = activeVideoIdRef.current;
      if (!videoId) return;

      const player = ytPlayersRef.current[String(videoId)];
      if (!player?.getCurrentTime) return;

      let now = 0;
      try {
        now = await player.getCurrentTime();
      } catch {
        return;
      }

      const prev = Number(lastPlaybackTimeRef.current || 0);
      let delta = now - prev;

      if (!Number.isFinite(delta) || delta <= 0) {
        lastPlaybackTimeRef.current = now;
        return;
      }

      if (delta > 15) {
        lastPlaybackTimeRef.current = now;
        return;
      }

      lastPlaybackTimeRef.current = now;

      if (!activeSessionIdRef.current) {
        beginWatchSession(videoId);
      }

      const openVideo = videos.find(
        (v) => String(v.youtube_video_id) === String(videoId)
      );

      const res = await apiFetch("/api/videos/watch-progress", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          viewerId,
          viewerRole,
          videoId,
          watchTimeDelta: delta,
          videoDuration:
            openVideo?.durationSeconds ||
            openVideo?.duration_seconds ||
            300,
          sessionId: activeSessionIdRef.current,
          eventId: nextEventId(),
          clientNow: new Date().toISOString(),
          clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
        }),
      });

      if (!res.ok) return;

      const data = await res.json().catch(() => ({}));
      clientExpectedRef.current += delta;

      const serverPercent = Number(
        data?.watchPercentage ?? data?.watch_percentage
      );
      if (Number.isFinite(serverPercent)) {
        setProgressByVideo((prevMap) => ({
          ...prevMap,
          [String(videoId)]: Math.max(0, Math.min(100, serverPercent)),
        }));
      }

      if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
        stopWatchTracking();
      }
    } catch {
      // ignore flush errors
    }
  };




  const startWatchTracking = (videoId, videoDuration) => {
    if (!viewerId || !viewerRole) return;

    if (watchIntervalRef.current) stopWatchTracking();

    beginWatchSession(videoId);

    const player = ytPlayersRef.current[String(videoId)];
    try {
      const t = player?.getCurrentTime?.();
      lastPlaybackTimeRef.current = Number.isFinite(t) ? t : 0;
    } catch {
      lastPlaybackTimeRef.current = 0;
    }

    watchIntervalRef.current = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (!isPlayingRef.current) return;

      const player = ytPlayersRef.current[String(videoId)];
      if (!player?.getCurrentTime) return;

      if (activeVideoIdRef.current !== String(videoId)) return;

      let now = 0;
      try {
        now = await player.getCurrentTime();
      } catch {
        return;
      }

      const prev = Number(lastPlaybackTimeRef.current || 0);
      let delta = now - prev;

      if (!Number.isFinite(delta) || delta <= 0) {
        lastPlaybackTimeRef.current = now;
        return;
      }

      if (delta > 15) {
        lastPlaybackTimeRef.current = now;
        return;
      }

      lastPlaybackTimeRef.current = now;

      if (!activeSessionIdRef.current) {
        beginWatchSession(videoId);
      }

      try {
        const res = await apiFetch("/api/videos/watch-progress", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            viewerId,
            viewerRole,
            videoId,
            watchTimeDelta: delta,
            videoDuration:
              videoDuration ||
              300,
            sessionId: activeSessionIdRef.current,
            eventId: nextEventId(),
            clientNow: new Date().toISOString(),
            clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
          }),
        });

        if (!res.ok) return;

        const data = await res.json();

        const serverPercent = Number(
          data?.watchPercentage ?? data?.watch_percentage
        );
        if (Number.isFinite(serverPercent)) {
          setProgressByVideo((prevMap) => ({
            ...prevMap,
            [String(videoId)]: Math.max(0, Math.min(100, serverPercent)),
          }));
        }

        clientExpectedRef.current += delta;
        const serverWatch = Number(data?.clampedWatchTimeSeconds);
        if (Number.isFinite(serverWatch)) {
          const diff = Math.abs(clientExpectedRef.current - serverWatch);
          if (diff > 2) {
            console.warn("❌ DRIFT", {
              videoId,
              clientExpected: clientExpectedRef.current,
              serverWatch,
              diff,
            });
          }
        }

        if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
          stopWatchTracking();
        }
      } catch {
        // ignore network errors
      }
    }, 5000);
  };




  //Helper
  const extractVideoId = (input) => {
    if (!input) return null;

    const trimmed = input.trim();

    const match =
      trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ||
      trimmed.match(/v=([a-zA-Z0-9_-]{11})/);

    return match ? match[1] : null;
  };

  // fallback thumbnail if backend doesn't provide thumbnail_url
  const getThumb = (v) => {
    if (v?.thumbnail_url) return v.thumbnail_url;
    if (!v?.youtube_video_id) return "";
    return `https://i.ytimg.com/vi/${v.youtube_video_id}/hqdefault.jpg`;
  };

  useEffect(() => {
    if (playerId || !profileSlug) return;

    const fetchPlayerIdBySlug = async () => {
      try {
        const res = await apiFetch(`/api/player-profile/${profileSlug}`);
        if (!res.ok) throw new Error("Failed to resolve player profile");
        const data = await res.json();
        if (data?.id) setResolvedPlayerId(data.id);
      } catch (err) {
        console.error("Failed to resolve playerId from slug", err);
      }
    };

    fetchPlayerIdBySlug();
  }, [playerId, profileSlug]);

  // fetch videos + hydrate viewer-specific fields if backend supports it
  const fetchPlayerVideos = async (pid, { preferViewerContext = true } = {}) => {
    const qs =
      preferViewerContext && viewerId && viewerRole
        ? `?viewerId=${encodeURIComponent(
          viewerId
        )}&viewerRole=${encodeURIComponent(viewerRole)}`
        : "";

    const vidsRes = await apiFetch(`/api/player-videos/${pid}${qs}`);
    if (!vidsRes.ok) throw new Error("Failed to load player videos");
    const vidsRaw = await vidsRes.json();
    return Array.isArray(vidsRaw) ? vidsRaw : [];
  };

  useEffect(() => {
    if (!playerId) return;
    if (viewerRole !== "player" && !profileSlug) return;

    let cancelled = false;

    const initialize = async () => {
      try {
        setInitializing(true);
        setError(null);

        // ✅ Fetch player details + videos IN PARALLEL
        const playerProfileRequest =
          viewerRole === "player"
            ? apiFetch(`/api/player-details/${playerId}`)
            : apiFetch(`/api/player-profile/${profileSlug}`);

        const [playerRes, vids] = await Promise.all([
          playerProfileRequest,
          fetchPlayerVideos(playerId, {
            preferViewerContext: viewerRole !== "player",
          }),
        ]);

        if (!playerRes.ok) throw new Error("Failed to load player details");

        const player = await playerRes.json();

        if (cancelled) return;

        setVideos(vids);

        // hydrate ratings + progress from response if present
        const initialRatings = {};
        const initialProgress = {};
        vids.forEach((v) => {
          const id = String(v.youtube_video_id);
          const vr = v.viewerRating ?? v.viewer_rating ?? null;
          if (vr != null) initialRatings[id] = vr;

          const wp = v.watchPercentage ?? v.watch_percentage ?? null;
          if (wp != null && Number.isFinite(Number(wp))) {
            initialProgress[id] = Math.max(0, Math.min(100, Number(wp)));
          }
        });
        setUserRatings(initialRatings);
        setProgressByVideo(initialProgress);

        // ✅ hydrate persistent reported state
        const initialReported = {};

        vids.forEach((v) => {
          const id = String(v.youtube_video_id);

          if (v.viewerHasReported) {
            initialReported[id] = true;
          }
        });

        setReportedVideos(initialReported);

        const hasId = !!(player.youtubeChannelId || player.youtube_channel_id);
        setHasChannelId(hasId);

        // compute noVideosFound based on has channel id + empty list
        setNoVideosFound(hasId && vids.length === 0);

        // ✅ IMPORTANT: don't block page load waiting for sync
        // Only owners can sync; run it in background and refresh videos after.
        // ✅ Only trigger sync if videos are empty (first load)
        if (hasId && isOwner && vids.length === 0) {
          apiFetch(`/api/sync-youtube/${playerId}`, { method: "POST" })
            .then(async (syncRes) => {
              if (!syncRes.ok) throw new Error("Sync failed");
              const refreshed = await fetchPlayerVideos(playerId, {
                preferViewerContext: false,
              });
              if (cancelled) return;

              setVideos(refreshed);

              // refresh rating/progress hydrations too
              const r2 = {};
              const p2 = {};
              refreshed.forEach((v) => {
                const id = String(v.youtube_video_id);
                const vr = v.viewerRating ?? v.viewer_rating ?? null;
                if (vr != null) r2[id] = vr;

                const wp = v.watchPercentage ?? v.watch_percentage ?? null;
                if (wp != null && Number.isFinite(Number(wp))) {
                  p2[id] = Math.max(0, Math.min(100, Number(wp)));
                }
              });
              setUserRatings(r2);
              setProgressByVideo(p2);

              setNoVideosFound(hasId && refreshed.length === 0);
            })
            .catch(() => {
              // ignore sync errors; UI still works from DB
            });
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("Initialization error:", err);
        setError("Failed to load player video profile.");
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [playerId, isOwner, viewerId, viewerRole]);

  const extractChannelIdentifier = (input) => {
    if (!input) return null;

    const trimmed = input.trim();

    if (/^UC[a-zA-Z0-9_-]{20,}$/.test(trimmed)) {
      return trimmed;
    }

    const matchChannel = trimmed.match(
      /youtube\.com\/channel\/([a-zA-Z0-9_-]+)/
    );
    if (matchChannel) return matchChannel[1];

    const matchHandle = trimmed.match(/youtube\.com\/@([a-zA-Z0-9_-]+)/);
    if (matchHandle) return matchHandle[1];

    return null;
  };

  const handleConnect = async () => {
    setError(null);
    setNoVideosFound(false);
    setLoading(true);

    if (!playerId) {
      setError("Player ID is missing. Please refresh the page or log in again.");
      setLoading(false);
      return;
    }

    const trimmedInput = channelUrl.trim();
    const videoId = extractVideoId(trimmedInput);

    try {
      if (videoId) {
        const syncRes = await apiFetch(`/api/sync-youtube/${playerId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ videoId }),
        });
        if (!syncRes.ok) {
          const text = await syncRes.text().catch(() => "");
          throw new Error(text || "We couldn’t find this channel yet.");
        }
      } else {
        const identifier = extractChannelIdentifier(trimmedInput);

        if (!identifier) {
          setError(
            "Please paste a valid YouTube channel link or Channel ID (UC...)."
          );
          setLoading(false);
          return;
        }

        const syncRes = await apiFetch(`/api/sync-youtube/${playerId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channelIdOrUsername: identifier }),
        });
        if (!syncRes.ok) {
          const text = await syncRes.text().catch(() => "");
          throw new Error(text || "We couldn’t find this channel yet.");
        }
      }

      const refreshed = await fetchPlayerVideos(playerId, {
        preferViewerContext: false,
      });
      setVideos(refreshed);
      setHasChannelId(true);

      if (refreshed.length === 0) setNoVideosFound(true);
      else setNoVideosFound(false);
    } catch (err) {
      console.error("YouTube sync error:", err);
      setError(err?.message || "We couldn’t find this channel yet.");
    } finally {
      setLoading(false);
    }
  };

  // ⭐ rating submit
  const handleRate = async (videoId, ratingValue) => {
    // block if not allowed (no id or player trying to rate)
    if (!viewerId || viewerRole === "player") return;

    const normalizedId = String(videoId);

    // optimistic UI update
    setUserRatings((prev) => ({
      ...prev,
      [normalizedId]: ratingValue,
    }));

    try {
      const res = await apiFetch("/api/rate-video", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          videoId,
          viewerId,
          viewerRole,
          rating: ratingValue,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Rating failed");
      }
    } catch (err) {
      console.error("❌ Failed to submit rating:", err);
    }
  };

  const showReportFeedback = (videoId, text) => {
    setReportFeedback({
      videoId: String(videoId),
      text,
    });

    if (showReportFeedback._t) {
      clearTimeout(showReportFeedback._t);
    }

    showReportFeedback._t = setTimeout(() => {
      setReportFeedback({
        videoId: null,
        text: "",
      });
    }, 2000);
  };

  const handleReportVideo = async (videoId) => {
    try {
      await apiFetch("/api/report-video", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          videoId,
          reporterId: viewerId,
          reporterRole: viewerRole,
        }),
      });

      setReportedVideos((prev) => ({
        ...prev,
        [videoId]: true,
      }));

      showReportFeedback(
        videoId,
        "Report received. Thank you."
      );
    } catch (err) {
      console.error("Failed to report video", err);
    }
  };

  const renderRating = (videoId) => {
    const canRate = !!viewerId && viewerRole !== "player";
    const key = String(videoId);
    const effectiveRating = userRatings[key] ?? 0;

    return (
      <div className="rating-stars">
        {[1, 2, 3, 4, 5].map((value) => (
          <span
            key={value}
            onClick={() => canRate && handleRate(videoId, value)}
            style={{
              color: value <= effectiveRating ? "#FFD700" : "#888",
              cursor: canRate ? "pointer" : "default",
              fontSize: "20px",
            }}
            role="button"
            aria-label={`Rate ${value} star`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // open/close player (do not remove videos; this page is a library)
  const togglePlayer = async (videoId) => {
    await flushActiveWatchProgress();
    stopWatchTracking();

    setOpenVideoId((prev) => (prev === videoId ? null : videoId));
  };

  // nice ordering: keep original order, but you can change later if you want
  const orderedVideos = useMemo(() => {
    return Array.isArray(videos) ? videos : [];
  }, [videos]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      (async () => {
        await flushActiveWatchProgress();
        stopWatchTracking();
      })();
    };
  }, []);

  return (
    <div
      className={`player-video-profile ${isRoleView ? "role-view" : "player-view"
        } ${location.pathname.includes("/player-video-profile/")
          ? "fullscreen-route"
          : "embedded-mode"
        }`}
    >
      <div className="video-profile-wrapper">
        <div className="header-top-row">
          <h2 className="video-profile-title">Player Video Profile</h2>

          {/* ⛶ Expand — ONLY EMBEDDED PLAYER VIEW */}
          {!isRoleView && (
            <button
              type="button"
              className="fullscreen-btn header-expand-btn"
              title={
                location.pathname.includes("/player-video-profile/")
                  ? "Close fullscreen"
                  : "Open fullscreen"
              }
              onClick={() => {

                // ✅ already in fullscreen route → CLOSE
                if (location.pathname.includes("/player-video-profile/")) {
                  navigate(-1);
                  return;
                }

                // ✅ open fullscreen route
                navigate(`/player-video-profile/${playerId}`, {
                  state: {
                    playerProfileSlug: profileSlug,
                    viewerRole,
                    viewerId,
                    playerId,
                  },
                });

              }}
            >
              {location.pathname.includes("/player-video-profile/")
                ? "✕"
                : "⛶"}
            </button>
          )}
        </div>

        {isOwner && (
          <>
            {initializing && (
              <div className="blur-overlay">
                <div className="overlay-content">
                  <p>Loading your video profile...</p>
                </div>
              </div>
            )}

            {!initializing && !hasChannelId && (
              <div className="blur-overlay fade-in">
                <div className="overlay-content">
                  <p>Connect your YouTube channel to showcase match highlights</p>

                  <p className="note">
                    <strong>
                      Please connect a YouTube channel dedicated to your football
                      match highlights (create if you don't have and upload your
                      video).
                    </strong>
                    <br />
                    <br />
                    Match highlights give scouts better insight than skill clips.
                    Keep highlight videos concise (ideally{" "}
                    <strong>2–6 minutes</strong>) and set to{" "}
                    <strong>Public or Unlisted</strong>.
                    <br />
                    <br />
                    Your channel should have{" "}
                    <strong>at least one match highlight video </strong>
                    to be visible to scouts.
                    <br />
                    <br />
                    <strong>Continue uploading highlights to this channel</strong>{" "}
                    — new videos will sync automatically and appear on your
                    profile.
                  </p>

                  <div className="input-button-row">
                    <input
                      type="text"
                      value={channelUrl}
                      onChange={(e) => setChannelUrl(e.target.value)}
                      placeholder="Paste YouTube channel link or Channel ID (e.g. https://youtube.com/@yourhandle or UCxxxx)"
                    />

                    <button onClick={handleConnect} disabled={loading}>
                      {loading ? "Connecting..." : "Connect to YouTube"}
                    </button>
                  </div>

                  {error && <p className="error-message">{error}</p>}
                </div>
              </div>
            )}
          </>
        )}

        <div className="video-profile-scroll-container fade-in">
          {/* 🆕 CRON / WAITING STATE */}
          {hasChannelId && videos.length === 0 && !loading && !noVideosFound && (
            <p className="no-videos-message">
              <strong>✅ YouTube channel connected.</strong>
              <br />
              <br />
              Synced videos will appear here once they are pulled from your
              YouTube channel.
              <br />
              <br />
              <strong>Important:</strong> Your videos must be set to{" "}
              <strong>Public or Unlisted</strong>. Private videos cannot be
              synced.
              <br />
              <br />
              To be visible to scouts, you must have{" "}
              <strong>at least one football match highlight video</strong> on
              your YouTube channel.
              <br />
              <br />
              If you already have public or unlisted highlight videos, don’t
              worry — they may not appear immediately.
              <br />
              <br />
              <strong>
                New videos are synced automatically, usually within 24 hours.
              </strong>
            </p>
          )}

          {!loading && hasChannelId && noVideosFound && isOwner && (
            <p className="no-videos-message">
              <strong>✅ YouTube channel connected successfully.</strong>
              <br />
              <br />
              No videos are currently available on your profile.
              <br />
              <br />
              If you have already uploaded public or unlisted match highlights,
              they may not appear immediately.
              <br />
              <br />
              <strong>
                Videos are synced automatically in the background, usually within
                24 hours.
              </strong>
              <br />
              <br />
              Until at least one match highlight video is synced, your profile
              will not be visible to scouts.
            </p>
          )}

          <div
            className={`video-profile-grid ${orderedVideos.length <= 2 ? "few-videos" : ""
              }`}
          >
            {orderedVideos.map((video) => {
              const id = String(video.youtube_video_id);
              const isOpen = openVideoId === id;
              const percent = Number(progressByVideo[id] ?? 0);

              // ✅ Posted label shown only to scout/agent/club (not player)
              const postedAt = toDate(video);
              const postedLabel = timeAgo(postedAt);
              const showPostedTag = viewerRole !== "player" && !!postedLabel;

              return (
                <div key={id} className="pvp-video-item">

                  <div className={`video-card ${isOpen ? "open" : ""}`}>

                    {reportFeedback.videoId === String(id) && (
                      <div className="report-feedback">
                        {reportFeedback.text}
                      </div>
                    )}
                  {!isOpen ? (
                    <div
                      className="thumbnail-container"
                      onClick={() => togglePlayer(id)}
                      style={{ position: "relative" }}
                    >
                      <img src={getThumb(video)} alt={video.title} />

                      {/* ✅ Posted badge (scout/agent/club only) */}
                      {showPostedTag && (
                        <div className="vp-posted-badge">{postedLabel}</div>
                      )}

                      {/* ▶ play overlay */}
                      <div className="play-button">▶</div>

                      {/* ✅ YouTube-style thumbnail progress bar */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          height: "6px",
                          background: "rgba(255,255,255,0.15)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.max(0, Math.min(100, percent))}%`,
                            background: "#ff0000",
                            transition: "width 200ms ease",
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="video-wrapper">
                        <YouTube
                          videoId={id}
                          className="youtube-iframe"
                          iframeClassName="youtube-iframe"
                          opts={{
                            width: "100%",
                            height: "100%",
                            playerVars: {
                              autoplay: 1,
                              modestbranding: 1,
                              rel: 0,
                              controls: 1,
                              fs: 1, // ✅ THIS is the missing key
                            },
                          }}
                          iframeProps={{
                            allowFullScreen: true,
                          }}

                        onReady={(e) => {
                          ytPlayersRef.current[id] = e.target;

                          try {
                            lastPlaybackTimeRef.current =
                              e.target.getCurrentTime() || 0;
                          } catch {
                            lastPlaybackTimeRef.current = 0;
                          }
                        }}


                          onStateChange={(e) => {
                            const isActive =
                              String(openVideoIdRef.current) === String(id);

                            if (!isActive) {
                              isPlayingRef.current = false;
                              return;
                            }

                            // 1 = PLAYING, 2 = PAUSED, 0 = ENDED
                            if (e.data === 1) {
                              isPlayingRef.current = true;

                              try {
                                lastPlaybackTimeRef.current =
                                  e.target.getCurrentTime() ||
                                  lastPlaybackTimeRef.current ||
                                  0;
                              } catch { }

                              if (!watchIntervalRef.current) {
                                startWatchTracking(
                                  id,
                                  video.durationSeconds ||
                                  video.duration_seconds ||
                                  300
                                );
                              }

                            } else if (e.data === 2) {
                              // ✅ PAUSED
                              (async () => {
                                await flushActiveWatchProgress();
                                isPlayingRef.current = false;
                              })();
                            } else if (e.data === 0) {
                              // ✅ ENDED
                              (async () => {
                                await flushActiveWatchProgress();
                                stopWatchTracking();
                              })();
                            } else {
                              isPlayingRef.current = false;
                            }
                          }}
                      />
                    </div>
                  )}

                  <h4>{video.title}</h4>

                </div>

              {/* ✅ Role-view action bar */ }
                {viewerRole !== "player" && (
                <div className="pvp-video-actions-bar">

                      <button
                        className="pvp-vsn-profile-btn"
                        title="Click to open Player Profile"
                        onClick={() => {
                          window.location.href = `/player-profile/${profileSlug}`;
                        }}
                      >
                        VSN
                      </button>

                      <div className="pvp-video-scout-rating">
                        <span className="pvp-rating-label">
                          Scout Rating
                        </span>

                        {renderRating(id)}
                      </div>

                      <button
                        className="pvp-report-video-btn"
                        type="button"
                        onClick={() => handleReportVideo(id)}
                        disabled={reportedVideos[id]}
                      >
                        {reportedVideos[id] ? "Reported" : "Report"}
                      </button>

                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerVideoProfile;
